import { Link } from '@tanstack/react-router';
import { TrendingUp } from 'lucide-react';
import { Page, PageHeader } from '@/components/ui/page';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
} from '@/components/ui/section';
import { EmptyState } from '@/components/ui/empty';
import { FormField } from '@/components/ui/form-field';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/spinner';
import { StatusPill } from '@/components/status-pill';
import { ForecastCreateButton } from '@/features/forecasts/components/forecast-create-button';
import { useProjects } from '@/features/projects/use-projects';
import { useForecasts } from '@/features/forecasts/use-forecasts';
import { relTime } from '@/lib/format';
import { useProjectFilterParam } from '@/lib/use-project-filter';

const PROJECT_FILTER_STORAGE = 'seo:forecasts-project';
const ALL_SENTINEL = '__all__';

export function ForecastsRoute(): JSX.Element {
  const projects = useProjects();
  const availableProjects = projects.data?.items ?? [];

  // Filter lives in the URL (?project=) — see useProjectFilterParam.
  const { projectId, setProjectId } = useProjectFilterParam({
    storageKey: PROJECT_FILTER_STORAGE,
    projects: availableProjects,
    projectsLoading: projects.isLoading,
  });

  const forecasts = useForecasts(projectId || undefined);
  const rows = forecasts.data?.items ?? [];

  return (
    <Page>
      <PageHeader
        title="Forecasts"
        description="Fits a baseline to history, models the impact of planned actions, returns 3 scenarios with confidence bands."
        actions={
          <ForecastCreateButton
            projects={availableProjects}
            defaultProjectId={projectId}
          />
        }
      />

      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <Section>
          <FormField label="Project filter">
            {availableProjects.length === 0 ? (
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>No projects yet.</p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/projects">Create one</Link>
                </Button>
              </div>
            ) : (
              <Select
                value={projectId || ALL_SENTINEL}
                onValueChange={(v) =>
                  setProjectId(v === ALL_SENTINEL ? '' : v)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_SENTINEL}>All projects</SelectItem>
                  {availableProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
        </Section>

        <Section>
          <SectionHeader>
            <div>
              <SectionTitle className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                Forecasts
              </SectionTitle>
              <SectionDescription>
                Baseline vs. scenario projections with confidence bands.
              </SectionDescription>
            </div>
          </SectionHeader>

          {forecasts.isLoading ? (
            <div className="flex justify-center p-10">
              <Spinner />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={TrendingUp}
              title="No forecasts yet"
              description="Generate one to see baseline vs scenarios with confidence bands."
              variant="inline"
              action={
                <ForecastCreateButton
                  projects={availableProjects}
                  defaultProjectId={projectId}
                  label="Generate your first forecast"
                  buttonProps={{ size: 'sm' }}
                />
              }
            />
          ) : (
            <ul className="space-y-2">
              {rows.map((f) => (
                <li key={f.id}>
                  <Link
                    to="/forecasts/$forecastId"
                    params={{ forecastId: f.id }}
                    className="group flex items-center justify-between gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
                  >
                    <div>
                      <div className="font-mono text-sm group-hover:text-primary">
                        fc-{f.id.slice(0, 8)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {f.horizon_days}-day horizon · created{' '}
                        {relTime(f.created_at)}
                      </div>
                    </div>
                    <StatusPill status={f.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </Page>
  );
}
